import { useId, useMemo, type ReactNode } from 'react'
import { AlertTriangle, Check, CircleDashed, Loader2, RefreshCw } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  type DataTableColumn,
} from '@atrvd/saas-ui'
import { useProfile } from '@/hooks/useProfile'
import { useSageAccess } from '@/hooks/useSageAccess'
import { SageNotConnected } from '@/components/banking/SageNotConnected'
import { SageBankAccountsTable } from '@/components/sage/SageBankAccountsTable'
import { useTablePagination } from '@/hooks/useTablePagination'
import {
  queryErrorDisplay,
  tableQueryState,
  type TableQueryStateProps,
} from '@/components/common/queryErrorDisplay'
import { useFacilities } from '@/hooks/useBanking'
import {
  isActiveSync,
  useSageBankAccounts,
  useSageChecksAggregateByEntity,
  useSageSyncStatus,
  useSyncSageBankAccounts,
  useSyncSageChecks,
  useSyncSageFacilities,
  type SageSyncStatus,
} from '@/hooks/useSageIntegration'
import type { SageBankAccountRow, SageSyncStateRow } from '@/lib/db-tenant'
import type { Facility } from '@/types/banking'
import { formatDateTime } from '@dcp/shared/utils/formatters'
import { parseSageSyncWarnings, type RenderedSageSyncWarning } from '@dcp/shared/schemas/integrations'
import { hasPermission, isPlatformStaff } from '@dcp/shared'
import {
  deriveFacilityStatus,
  formatWarning,
  bankAccountCoverageByFacility,
  type FacilityStatus,
  type FacilityBankAccountCoverage,
  type CheckAggregate,
} from '@/lib/sage-utils'

const NO_FACILITIES: Facility[] = []
const NO_SAGE_BANK_ACCOUNTS: SageBankAccountRow[] = []

/**
 * Settings page for the Sage Intacct integration. The platform shares a
 * single Sage Intacct connection (credentials live in env vars on the
 * backend); this page surfaces the per-organization Facilities, Bank Accounts
 * and Checks sync status, lets administrators trigger manual runs, and owns the
 * per-account Include in Cash Position choice. Facility ↔ Sage location
 * mapping happens on the Settings → Facilities page.
 */
export function SageIntacctPage() {
  const { data: profile } = useProfile()
  const { isEnabled: sageEnabled, organizationName: sageOrganizationName } = useSageAccess()
  const sync = useSageSyncStatus()
  const syncChecks = useSyncSageChecks()
  const syncBankAccounts = useSyncSageBankAccounts()
  const syncFacilities = useSyncSageFacilities()
  // Full list (incl. inactive): the table resolves each account's WINNING
  // facility from it, and a location shared by an active and an inactive
  // facility must resolve to the active one. An active-only list would turn
  // an inactive facility's accounts into "unmapped" rows that stay visible.
  // The table itself hides the accounts of inactive facilities (ADR 0016);
  // `activeFacilities` below is the selector-shaped view.
  const facilitiesQuery = useFacilities({ activeOnly: false })
  const facilities = facilitiesQuery.data ?? NO_FACILITIES
  const sageBankAccountsQuery = useSageBankAccounts({ activeOnly: false })
  const sageBankAccounts = sageBankAccountsQuery.data ?? NO_SAGE_BANK_ACCOUNTS
  // Server-side aggregate (RPC public.sage_checks_aggregate_by_entity).
  // Returns ≤ N rows where N is the count of distinct financial_entity
  // values in tenant.sage_checks — typically tens, never the full register.
  //
  // History: this used to be `useSageChecks()` + `aggregateChecksByEntity`
  // on the client. PostgREST's `max_rows = 1000` (supabase/config.toml)
  // silently truncated the list query on tenants with > 1k checks, and
  // the aggregator then under-counted everything because the missing
  // ~90% of rows weighed nothing on the count. The RPC moves the GROUP BY
  // into SQL so the result is independent of row limits.
  // See migration 20260601120000_sage_checks_aggregate_rpc.sql.
  const { data: checksAggregateRows = [], error: sageChecksError } = useSageChecksAggregateByEntity()

  // The facilities and bank-accounts lists show their own loading skeleton and
  // error with Retry inside their tables. The check aggregate has no table of
  // its own — it feeds the check counts and the orphan note — so its failure
  // is the one load error raised at page level; otherwise those counts would
  // read as "no checks".
  //
  // `sync.error` is intentionally NOT a load error: it's a polling hook that
  // re-runs every 3s, so a transient network blip would raise a misleading
  // destructive alert even though all the data is on screen. Polling errors
  // get a softer warning alert below.
  const pollError = sync.error instanceof Error ? sync.error : null

  /**
   * Who may trigger a Sage sync. Two independent paths, mirroring
   * `canInviteUsers` in @dcp/shared:
   *
   *  - platform staff keep the privilege on their system role alone, since
   *    their `product_role` may legitimately be null. The role list itself is
   *    NOT repeated here — `isPlatformStaff` owns it, so this check and the
   *    edge function's cannot drift apart (Presto PR #201 finding 4);
   *  - anyone holding the `manage_settings` product permission — i.e. the
   *    tenant's own Admin, whose system role is plain `'user'`.
   *
   * DCP-349: the second path was missing, so a Client Admin — the person who
   * owns this integration inside their organization — reached the page (the
   * route is gated only by RequireMFA) but found every Sync button replaced by
   * read-only status. Gating on the permission rather than on a hardcoded role
   * list also keeps this in step with the rest of Settings, which is routed
   * through `RequirePermission permission="manage_settings"`.
   */
  const canSync =
    isPlatformStaff(profile?.role) || hasPermission(profile?.productRole, 'manage_settings')

  // Per-type syncing flags. `sync.isSyncing` checks "any row is active" which
  // conflates the two sync types — keeping the per-type check via `isActiveSync`
  // on the matching row is enough and reads more honestly. `isActiveSync` treats
  // both a fresh 'pending' row (edge claimed the sync, task not yet started) and
  // a 'syncing' row as in-progress, and auto-frees once a 'pending' row goes
  // stale — so a never-started sync can't disable the button forever. The
  // mutation's `isPending` covers the moment between the trigger click and the
  // state-row appearing/flipping, so neither signal can be dropped.
  const isChecksSyncing =
    syncChecks.isPending || (sync.checks != null && isActiveSync(sync.checks))

  const isBankAccountsSyncing =
    syncBankAccounts.isPending ||
    (sync.bankAccounts != null && isActiveSync(sync.bankAccounts))

  // Facilities auto-import in-progress flag (DCP-262 AC#2). Same shape as the
  // bank-accounts flag: the mutation's `isPending` covers the click→row-appears
  // gap, and `isActiveSync` keeps the button disabled while the 'facilities'
  // sage_sync_state row is 'syncing' or freshly 'pending'.
  const isFacilitiesSyncing =
    syncFacilities.isPending ||
    (sync.facilities != null && isActiveSync(sync.facilities))

  // Attributed through the winner facility of each location key, so a losing
  // case-variant facility shows 0 like it does in Bank Accounts and Cash Position.
  const coverageByFacility = useMemo(
    () => bankAccountCoverageByFacility(sageBankAccounts, facilities),
    [sageBankAccounts, facilities],
  )

  // Only ACTIVE facilities take part in the Sage bank-accounts / checks sync
  // (DCP-262: facilities auto-imported from Sage arrive inactive and the admin
  // activates just the ones they use). The coverage table must show that same
  // active set — otherwise every not-yet-activated import shows up as noisy
  // "Not synced yet / 0 accounts" rows that will never sync. The Settings →
  // Facilities admin list still shows all facilities so they can be activated.
  const activeFacilities = useMemo(
    () => facilities.filter((f) => f.is_active),
    [facilities],
  )

  // Whether the bank-accounts sync has ever produced a result. Used to
  // distinguish "not synced yet" from "synced and unmatched".
  const bankAccountsSyncRanAtLeastOnce = !!sync.bankAccounts?.last_sync_at

  const bankAccountWarnings = parseSageSyncWarnings(sync.bankAccounts?.warnings)
  const checksWarnings = parseSageSyncWarnings(sync.checks?.warnings)
  const facilitiesWarnings = parseSageSyncWarnings(sync.facilities?.warnings)

  // Map aggregate rows into the `Map<financial_entity, CheckAggregate>` shape
  // the bank-accounts table looks rows up in.
  const checkAggByEntity = useMemo<Map<string, CheckAggregate>>(() => {
    const m = new Map<string, CheckAggregate>()
    for (const r of checksAggregateRows) {
      m.set(r.financial_entity, {
        total: r.total,
        outstanding: r.outstanding_count,
        outstandingAmount: Number(r.outstanding_amount),
      })
    }
    return m
  }, [checksAggregateRows])

  // Orphan checks: records whose financial_entity is not present in the current
  // sage_bank_accounts catalog. Surfaced as a footer note so operators notice
  // when a bank account was removed from Sage but its checks linger here.
  // Measured against the FULL catalog, not the table's visible rows: an account
  // hidden because its facility is inactive still exists, so its checks are not
  // orphans.
  //
  // Memoised for the same reason as the four aggregators above — without
  // useMemo, every unrelated re-render (tab switch, toast, parent context
  // update) would rebuild the Set and re-filter the check array. The Set
  // build is O(n) over bank accounts; the filter is O(n) over checks; for
  // tenants with hundreds of checks this is the difference between a free
  // no-op and a full sweep per render.
  const knownEntityIds = useMemo(
    () => new Set(sageBankAccounts.map((ba) => ba.financial_entity_id)),
    [sageBankAccounts],
  )
  // Sum total-check counts across aggregate rows whose financial_entity is
  // NOT in the current sage_bank_accounts catalog. Replaces the prior
  // client-side filter over raw sage_checks; the aggregate already contains
  // one row per entity with the per-entity total, so we just add them up.
  const orphanCheckCount = useMemo(
    () =>
      checksAggregateRows
        .filter((r) => !knownEntityIds.has(r.financial_entity))
        .reduce((sum, r) => sum + r.total, 0),
    [checksAggregateRows, knownEntityIds],
  )

  const checksSyncRanAtLeastOnce = !!sync.checks?.last_sync_at

  // DCP-368: one gate for the whole page, deliberately not per control.
  //
  // Sage credentials are platform-wide env vars, so this page used to render as
  // "connected" for every tenant — including one created for a different client,
  // whose syncs then pulled another client's Sage company into its schema.
  //
  // The answer is immediate; the queries above still fire, and their 403s are simply never rendered. Not worth
  // threading an `enabled` flag through six hooks to avoid.
  if (!sageEnabled) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sage Intacct</h1>
        </div>
        <SageNotConnected organizationName={sageOrganizationName} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Sage Intacct</h1>
        <p className="text-muted-foreground">
          Sync facilities, bank accounts and checks from Sage Intacct. Facility settings are managed under Settings → Facilities.
        </p>
      </div>

      {sageChecksError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load check counts</AlertTitle>
          <AlertDescription>{sageChecksError.message}</AlertDescription>
        </Alert>
      )}

      {/* Softer warning when only the 3s polling refresh is failing —
          the already-loaded tables on screen are still authoritative,
          but the user should know the status panel is stale. */}
      {pollError && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Sync status updates paused</AlertTitle>
          <AlertDescription>
            Couldn't refresh the live sync status: {pollError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Facilities first (DCP-262): pulling Sage locations into DCP is the
          first step. Imported facilities land on Settings → Facilities as
          inactive rows with Source = "Sage Intacct". */}
      <SyncSection
        title="Facilities"
        description="Imports all of your Sage Intacct locations into DCP as facilities. Each facility gets its Sage location name as its Legal Name and a short Display Name; you can edit both. New facilities arrive inactive — activate them and assign them to packs under Settings → Facilities."
        syncLabel="Sync Facilities"
        row={sync.facilities}
        isSyncing={isFacilitiesSyncing}
        canSync={canSync}
        onSync={() => syncFacilities.mutate()}
        warningsTitle="Import warnings"
        warnings={facilitiesWarnings}
      >
        <FacilityCoverageTable
          facilities={activeFacilities}
          coverageByFacility={coverageByFacility}
          syncRanAtLeastOnce={bankAccountsSyncRanAtLeastOnce}
          {...tableQueryState(facilitiesQuery, activeFacilities.length)}
        />
      </SyncSection>

      <SyncSection
        title="Bank Accounts"
        description="Refreshes the list of bank accounts from Sage Intacct. Their Intacct balances and checks are imported based on this list."
        syncLabel="Sync Bank Accounts"
        row={sync.bankAccounts}
        isSyncing={isBankAccountsSyncing}
        canSync={canSync}
        onSync={() => syncBankAccounts.mutate()}
        warningsTitle="Coverage warnings"
        warnings={bankAccountWarnings}
      >
        <SageBankAccountsTable
          accounts={sageBankAccounts}
          accountsQuery={tableQueryState(sageBankAccountsQuery, sageBankAccounts.length)}
          facilities={facilities}
          facilitiesQuery={tableQueryState(facilitiesQuery, facilities.length)}
          checksByEntity={checkAggByEntity}
          checksSyncRanAtLeastOnce={checksSyncRanAtLeastOnce}
          canEdit={canSync}
        />
      </SyncSection>

      <SyncSection
        title="Checks"
        description="Refreshes the outstanding checks pulled from Sage Intacct."
        syncLabel="Sync Checks"
        row={sync.checks}
        isSyncing={isChecksSyncing}
        canSync={canSync}
        onSync={() => syncChecks.mutate()}
        warningsTitle="Coverage warnings"
        warnings={checksWarnings}
      >
        {/* Orphans: checks whose Sage bank account is no longer in our catalog.
            Without a loaded catalog every check would read as an orphan. */}
        {sageBankAccountsQuery.isSuccess && orphanCheckCount > 0 && (
          <p className="text-xs text-muted-foreground">
            {orphanCheckCount} check{orphanCheckCount === 1 ? '' : 's'} reference Sage
            bank accounts no longer in your catalog (likely deleted in Sage after a previous sync).
          </p>
        )}
      </SyncSection>
    </div>
  )
}

interface SyncSectionProps {
  title: string
  description: string
  syncLabel: string
  row: SageSyncStateRow | null
  isSyncing: boolean
  /** Without it the sync button is replaced by an explanation. */
  canSync: boolean
  onSync: () => void
  warningsTitle: string
  warnings: RenderedSageSyncWarning[]
  children?: ReactNode
}

/**
 * One sync type's card: title, description and the run's meta line on the
 * left, the sync button on the right, then the failure, the sync warnings and
 * the section's own content. The card is a labelled region so each section is
 * reachable by its title.
 */
function SyncSection({
  title,
  description,
  syncLabel,
  row,
  isSyncing,
  canSync,
  onSync,
  warningsTitle,
  warnings,
  children,
}: SyncSectionProps) {
  const titleId = useId()
  return (
    <Card role="region" aria-labelledby={titleId}>
      <CardHeader>
        {/* Stacks only on narrow screens; from sm up the text shrinks and the button keeps its place. */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 space-y-1.5">
            <CardTitle id={titleId}>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
            <SyncMetaLine row={row} />
          </div>
          {canSync ? (
            <Button onClick={onSync} disabled={isSyncing} className="shrink-0">
              {isSyncing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {syncLabel}
                </>
              )}
            </Button>
          ) : (
            <p className="shrink-0 text-sm text-muted-foreground">
              Only administrators can trigger a sync.
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {row?.last_error && row.sync_status === 'failed' && (
          <div className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            {row.last_error}
          </div>
        )}

        {warnings.length > 0 && (
          <Alert variant="warning">
            <AlertTitle>{warningsTitle}</AlertTitle>
            <AlertDescription>
              <ul className="ml-4 list-disc space-y-1">
                {warnings.map((warning, idx) => (
                  <li key={idx}>{formatWarning(warning)}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {children}
      </CardContent>
    </Card>
  )
}

/** "Last run: <datetime> · N items synced · Sync status: <badge>" for one sync type. */
function SyncMetaLine({ row }: { row: SageSyncStateRow | null }) {
  const itemsSynced = row?.items_synced ?? 0
  return (
    // A <div>, not a <p>: the status Badge renders a <div>, which a <p> may not contain.
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
      <span>Last run: {row?.last_sync_at ? formatDateTime(row.last_sync_at) : '—'}</span>
      <span aria-hidden="true">·</span>
      <span>
        {itemsSynced} {itemsSynced === 1 ? 'item' : 'items'} synced
      </span>
      <span aria-hidden="true">·</span>
      <span className="inline-flex items-center gap-1.5">
        Sync status: <StatusBadge status={(row?.sync_status as SageSyncStatus) ?? 'idle'} />
      </span>
    </div>
  )
}

/** Minimal facility shape used by the coverage table — matches useFacilities(). */
interface CoverageFacility {
  id: string
  name: string
  sage_location_id: string | null
}

interface FacilityCoverageTableProps extends TableQueryStateProps {
  facilities: CoverageFacility[]
  /** Keyed by facility id; a facility owning no account is absent. */
  coverageByFacility: ReadonlyMap<string, FacilityBankAccountCoverage>
  syncRanAtLeastOnce: boolean
}

/** One coverage-table row: a facility with its derived Sage bank-account coverage. */
interface FacilityCoverageRow {
  facility: CoverageFacility
  count: number
  lastSeenAt: string | null
  status: FacilityStatus
}

const isMutedStatus = (status: FacilityStatus) => status === 'not_configured' || status === 'pending'

const FACILITY_COVERAGE_COLUMNS: DataTableColumn<FacilityCoverageRow>[] = [
  {
    key: 'name',
    header: 'Display Name',
    render: (row) => row.facility.name,
    cellClassName: (row) => (isMutedStatus(row.status) ? 'text-muted-foreground' : 'font-medium'),
  },
  {
    key: 'sage_location_id',
    header: 'Sage Location ID',
    render: (row) =>
      row.facility.sage_location_id ? (
        <span className="font-mono text-sm">{row.facility.sage_location_id}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'count',
    header: 'Bank accounts',
    render: (row) => (
      <span className={row.count > 0 ? 'font-medium' : 'text-muted-foreground'}>{row.count}</span>
    ),
  },
  {
    key: 'lastSeenAt',
    header: 'Last seen in Sage',
    render: (row) => (row.lastSeenAt ? formatDateTime(row.lastSeenAt) : '—'),
    cellClassName: 'text-sm text-muted-foreground',
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => <FacilityStatusBadge status={row.status} />,
  },
]

/**
 * Renders one row per facility showing how many Sage bank accounts are
 * currently mapped to its Sage location, when the location was last seen in
 * Sage, and a clear Status badge that tells the operator whether this facility
 * is wired up correctly to Sage.
 */
function FacilityCoverageTable({
  facilities,
  coverageByFacility,
  syncRanAtLeastOnce,
  loading,
  refreshing,
  error,
  retrying,
  onRetry,
}: FacilityCoverageTableProps) {
  // Client-side pagination: facilities are already loaded in full. The hook
  // follows the list back when it shrinks under the current page.
  const pagination = useTablePagination({ totalItems: facilities.length })
  const pagedRows: FacilityCoverageRow[] = pagination.paginate(facilities).map((facility) => {
    const coverage = coverageByFacility.get(facility.id)
    const count = coverage?.count ?? 0
    return {
      facility,
      count,
      lastSeenAt: coverage?.lastSeenAt ?? null,
      status: deriveFacilityStatus({
        sageLocationId: facility.sage_location_id,
        count,
        syncRanAtLeastOnce,
      }),
    }
  })

  // A failed facilities query must not read as "No active facilities".
  const { banner, tableError } = queryErrorDisplay({ error, retrying, onRetry }, facilities.length)

  return (
    <>
      {banner}
      <DataTable<FacilityCoverageRow>
        columns={FACILITY_COVERAGE_COLUMNS}
        data={pagedRows}
        rowKey={(row) => row.facility.id}
        pagination={pagination.dataTablePagination}
        itemName="facilities"
        loading={loading}
        refreshing={refreshing}
        error={tableError}
        emptyState={
          <p className="text-sm text-muted-foreground">
            No active facilities. Activate a facility in Settings &rarr; Facilities
            for it to sync Sage bank accounts.
          </p>
        }
      />
    </>
  )
}

/**
 * Visual badge for a facility's per-row sync status.
 */
function FacilityStatusBadge({ status }: { status: FacilityStatus }) {
  switch (status) {
    case 'synced':
      return (
        <Badge variant="success">
          <Check className="mr-1 h-3 w-3" />
          Synced
        </Badge>
      )
    case 'unmatched':
      return (
        <Badge variant="warning">
          <AlertTriangle className="mr-1 h-3 w-3" />
          No match
        </Badge>
      )
    case 'pending':
      return (
        <Badge variant="secondary">
          <CircleDashed className="mr-1 h-3 w-3" />
          Not synced yet
        </Badge>
      )
    case 'not_configured':
      return (
        <Badge variant="warning">
          <AlertTriangle className="mr-1 h-3 w-3" />
          Not configured
        </Badge>
      )
  }
}

/**
 * Maps a sage_sync_state.sync_status value into a visual badge.
 * Idle / pending / syncing / completed / failed are the states the migration
 * enforces. 'pending' is the transient "claimed, waiting for the task to start"
 * state set by the edge function; it reads as in-progress just like 'syncing'.
 */
function StatusBadge({ status }: { status: SageSyncStatus }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="secondary">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Pending
        </Badge>
      )
    case 'syncing':
      return (
        <Badge variant="secondary">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Syncing
        </Badge>
      )
    case 'completed':
      return (
        <Badge variant="success">
          <Check className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>
    default:
      return <Badge variant="secondary">Idle</Badge>
  }
}
